import excelJS from "exceljs";
import { conradEnergyFreshGreenColour, conradEnergyYellowColour } from "../theme/themeBrand";

enum SummaryType {
    isTotal = 1,
    isAverage = 2
}
export interface ColumnHeader {
    key: string,
    displayLabel: string,
    columnWidth: number,
    isDecimalFormat: boolean,
    preferredCurrency?: string,
    isTotal?: boolean,
    isAverage?: boolean,
    isSummaryRowRequired?: boolean
}

export interface DownloadFileNames {
    titleName: string,
    fileName: string,
    appendCurrentDateTime?: boolean
}

export async function downloadXlsxData<T extends Record<string, any>>(data: T[], columns: ColumnHeader[], titleName: string, sheetName: string, fileName: string) {
    if (!data?.length) {
        console.warn("No data for " + sheetName);
        return;
    }

    // Create workbook and worksheet
    const workbook = new excelJS.Workbook();
    const worksheet = workbook.addWorksheet(sheetName);

    addSheetTitle(worksheet, titleName, columns.length);

    const headerLabels = getHeaderLabels(columns);
    const headerRow = addHeaderRow(worksheet, headerLabels);

    setColumnWidths(worksheet, columns);

    addDataRows(worksheet, data, columns);

    addSummaryRow(worksheet, columns, headerRow.number, data.length);

    freezeHeaderRow(worksheet);

    addBordersToCells(worksheet);

    await exportWorkbook(workbook, fileName);
}

// Add sheet title
function addSheetTitle(worksheet: excelJS.Worksheet, title: string, columnCount: number) {
    const titleRow = worksheet.addRow([title]);
    worksheet.mergeCells(1, 1, 1, columnCount);
    titleRow.font = { size: 16, bold: true };
    titleRow.alignment = { horizontal: "center", vertical: "middle" };
}

// Get column header row names
function getHeaderLabels(columns: ColumnHeader[]): string[] {
    return columns.map(col => typeof col === "string" ? col : col.displayLabel);
}

// Define worksheet column width based on header length
function addHeaderRow(worksheet: excelJS.Worksheet, labels: string[]): excelJS.Row {
    const headerRow = worksheet.addRow(labels);
    headerRow.font = { bold: true, size: 12 };
    headerRow.alignment = { vertical: "middle", horizontal: "center" };
    return headerRow;
}

// Define worksheet column width based on header length
function setColumnWidths(worksheet: excelJS.Worksheet, columns: ColumnHeader[]) {
    worksheet.columns = columns.map((col) => {
        return {
            width: col.columnWidth ?? col.displayLabel.length + 10,
        };
    });
}

// Add data rows to the sheet
function addDataRows<T>(worksheet: excelJS.Worksheet, data: T[], columns: ColumnHeader[]) {
    data.forEach((row: any) => {
        const rowValues = columns.map((col) =>
            typeof col === "string" ? row[col] : row[col.key]
        );
        const addedRow = worksheet.addRow(rowValues);

        // Apply decimal formatting if configured 
        addedRow.eachCell((cell, colNumber) => {
            const colConfig = columns[colNumber - 1];
            if (typeof colConfig !== "string" && colConfig.isDecimalFormat && typeof cell.value === "number") {
                cell.numFmt = "#,##0.00";
            }
        });
    });
}

// Add Summary rows
function addSummaryRow(worksheet: excelJS.Worksheet, columns: ColumnHeader[], headerRowNumber: number, dataLength: number) {
    const startDataRow = headerRowNumber + 1;
    const endDataRow = startDataRow + dataLength - 1;

    const hasTotalColumnsOnly = columns
        .slice(1)
        .every((col) => typeof col !== "string" && col.isSummaryRowRequired && col.isTotal);

    if (hasTotalColumnsOnly) {
        // Add a single row if only the TOTAL is required
        totalSummaryRow(worksheet, columns, startDataRow, endDataRow);
        return;
    }

    averageTotalSummaryRows(worksheet, columns, startDataRow, endDataRow);
}

function totalSummaryRow(worksheet: excelJS.Worksheet, columns: ColumnHeader[], startDataRow: number, endDataRow: number) {
    const totalRow = worksheet.addRow([]);
    totalRow.getCell(1).value = "TOTAL";
    totalRow.getCell(1).font = { bold: true };

    columns.forEach((col, index) => {
        const isObjectColumn = typeof col !== "string";

        // skip first column (Date column) and if no summary required
        if (index === 0 || (isObjectColumn && !col.isSummaryRowRequired)) {
            return;
        }

        const column = worksheet.getColumn(index + 1);
        const cell = totalRow.getCell(index + 1);

        // Add SUM formula
        if (isObjectColumn && col.isTotal) {
            cell.value = {
                formula: `SUM(${column.letter}${startDataRow}:${column.letter}${endDataRow})`,
            };
        }

        //Formatting
        commonCellPattern(cell, col);
        summaryCellPattern(cell, SummaryType.isTotal);
    });

    totalRow.eachCell((cell) => {
        summaryCellPattern(cell, SummaryType.isTotal);
    });
}

function averageTotalSummaryRows(worksheet: excelJS.Worksheet, columns: ColumnHeader[], startDataRow: number, endDataRow: number) {
    const labelRow = worksheet.addRow([]);
    const valueRow = worksheet.addRow([]);

    let totalLabelPlaced = false;
    let averageLabelPlaced = false;

    columns.forEach((col, index) => {
        const isObjectColumn = typeof col !== "string";
        if (index === 0 || (isObjectColumn && !col.isSummaryRowRequired)) {
            return;
        }

        const column = worksheet.getColumn(index + 1);
        const labelCell = labelRow.getCell(index + 1);
        const valueCell = valueRow.getCell(index + 1);

        if (isObjectColumn && col.isAverage) {
            if (!averageLabelPlaced) {
                labelCell.value = "AVERAGE";
                summaryCellPattern(labelCell, SummaryType.isAverage);
                averageLabelPlaced = true;
            }

            const averageValue = getNumericSummaryValue(worksheet, index + 1, startDataRow, endDataRow, SummaryType.isAverage);
            valueCell.value = { formula: `IFERROR(AVERAGE(${column.letter}${startDataRow}:${column.letter}${endDataRow}), "-")`, result: averageValue ?? "-" };
            summaryCellPattern(valueCell, SummaryType.isAverage);
            commonCellPattern(valueCell, col);
        }
        else if (isObjectColumn && col.isTotal) {
            if (!totalLabelPlaced) {
                labelCell.value = "TOTAL";
                summaryCellPattern(labelCell, SummaryType.isTotal);
                totalLabelPlaced = true;
            }

            const totalValue = getNumericSummaryValue(worksheet, index + 1, startDataRow, endDataRow, SummaryType.isTotal);
            valueCell.value = { formula: `SUM(${column.letter}${startDataRow}:${column.letter}${endDataRow})`, result: totalValue ?? 0 };
            summaryCellPattern(valueCell, SummaryType.isTotal);
            commonCellPattern(valueCell, col);
        }
        else {
            labelCell.value = null;
            valueCell.value = null;
        }
    });

    // Style all cells with borders
    [labelRow, valueRow].forEach((row) => {
        columns.forEach((col, i) => {
            if (typeof col !== "string" && !col.isSummaryRowRequired) {
                return;
            }

            const cell = row.getCell(i + 1);
            cell.border = cell.border || cellBorders();
        });
    });
}

function getNumericSummaryValue(worksheet: excelJS.Worksheet, columnIndex: number, startDataRow: number, endDataRow: number, summaryType: SummaryType) {
    const values = worksheet.getColumn(columnIndex).values
        .slice(startDataRow, endDataRow + 1)
        .map((value) => typeof value === "number" ? value : Number(value))
        .filter((value) => Number.isFinite(value));

    if (!values.length) {
        return null;
    }

    if (summaryType === SummaryType.isAverage) {
        return values.reduce((sum, value) => sum + value, 0) / values.length;
    }

    if (summaryType === SummaryType.isTotal) {
        return values.reduce((sum, value) => sum + value, 0);
    }
}

function commonCellPattern(cell: excelJS.Cell, col: ColumnHeader) {
    // Apply decimal formatting if configured 
    if (typeof col !== "string" && col.isDecimalFormat) {
        cell.numFmt = "#,##0.00";
    }

    // Apply currency formatting if configured 
    if (typeof col !== "string" && col.preferredCurrency != null) {
        cell.numFmt = `${col.preferredCurrency} #,##0.00`;;
    }
}

function summaryCellPattern(cell: excelJS.Cell, cellType: SummaryType) {
    const colour = (cellType === SummaryType.isTotal)
        ? conradEnergyFreshGreenColour.main.replace("#", "")
        : (cellType === SummaryType.isAverage)
            ? conradEnergyYellowColour.main.replace("#", "")
            : "";

    cell.font = { bold: true, size: 12 };
    cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: colour },
    };
    cell.border = cellBorders();
}

function cellBorders(): Partial<excelJS.Borders> {
    return {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
    };
}

// Freeze header row
function freezeHeaderRow(worksheet: excelJS.Worksheet) {
    worksheet.views = [{ state: "frozen", xSplit: 1, ySplit: 2 }];
}

// Add borders to all cells
function addBordersToCells(worksheet: excelJS.Worksheet) {
    worksheet.eachRow(row => {
        row.eachCell(cell => {
            cell.border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" },
            };
        });
    });
}

// Create and download the file
async function exportWorkbook(workbook: excelJS.Workbook, filename: string) {
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${filename}.xlsx`;
    link.click();
    window.URL.revokeObjectURL(url);
}